import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-day-picker/lib/style.css';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector, useStore } from 'react-redux';
import * as Yup from 'yup';
import { clearAmenity, getAmenityV2, getBuildingAmenitiesV2 } from '../../../redux/actions/amenities';
import {
  addBookingV3,
  clearAmenityAvailabilityV2,
  clearBookingError,
  getBookingsCalendarV3,
  getBookingsListV3
} from '../../../redux/actions/bookingParAm';
import { GET_AMENITY_AVAILABILITY_V2 } from '../../../redux/constants';
import { requestFail, requestPending, requestSuccess } from '../../../utils/status';
import {
  AVAILABILITY_QUERY_TYPE_BLOCK,
  BOOKING_FORM_NO_TIME,
  buildBlockCreatePayload,
  buildBlockValidationSchema
} from './bookingFormHelpers';
import { useBlockDetailForm } from './useBlockDetailForm';
import { changeOpenedModal } from '../../../redux/actions/headerModal';
import { setToast } from '../../../redux/actions/window';
import { delay } from 'lodash';
import { useTranslation } from 'react-i18next';
import { WizardModalLayout } from '../../../components/ModalScrollProgress';
import {
  getBookingWizardModalProps,
  StyledModalCardV2
} from '../../../components/modalV2';
import AddBlockConfirmationStepV2 from './AddBlockConfirmationStepV2';
import AddBlockDetailStepV2 from './AddBlockDetailStepV2';
import { buildListV3Params } from './bookingsFilterHelpers';
import { useBookingAvailabilityFetcher } from '../../properties/Unit&Amenities/Amenities/V2/useBookingAvailabilityFetcher';
import {
  BOOKING_WIZARD_STEP_COUNT,
  getAddBlockWizardChromeProps
} from './bookingWizardConfigV2';
import WizardChromeV2 from '../../../components/WizardV2/WizardChromeV2';
import {
  WizardFooterButtonPrimary,
  WizardFooterButtonSecondary
} from '../../../components/WizardV2/wizardStylesV2';

const AddBlockV2 = () => {
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);

  const dispatch = useDispatch();
  const store = useStore();

  const { error, availabilitySlotsV2, status: bookingStatus } = useSelector(
    (state) => state.parAmBooking
  );
  const availabilitySlots = availabilitySlotsV2;
  const slotsLoading = bookingStatus === requestPending(GET_AMENITY_AVAILABILITY_V2);
  const { currentAmenityType } = useSelector((state) => state.services);
  const { modal } = useSelector((state) => state.headerModal);
  const { amenities, amenity } = useSelector((state) => state.amenities);

  const [verifyTimeslot, setVerifyTimeslot] = useState(false);

  const validationSchema = useMemo(
    () =>
      buildBlockValidationSchema({
        yup: Yup,
        t,
        amenity,
        availabilitySlots,
        verifyTimeslot
      }),
    [t, verifyTimeslot, availabilitySlots, amenity]
  );

  const schemaRef = useRef(validationSchema);
  schemaRef.current = validationSchema;

  const dynamicYupResolver = useCallback((values, context, options) => {
    return yupResolver(schemaRef.current)(values, context, options);
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    setValue,
    formState: { errors },
    clearErrors
  } = useForm({
    resolver: dynamicYupResolver,
    mode: 'onChange',
    defaultValues: {
      startTime: BOOKING_FORM_NO_TIME,
      endTime: BOOKING_FORM_NO_TIME,
      endDate: undefined,
      reason: ''
    }
  });

  const watchAmenityId = watch('amenity_id');
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');
  const watchBuilding = watch('building');
  const watchStartTime = watch('startTime');
  const watchEndTime = watch('endTime');

  useBookingAvailabilityFetcher({
    watchStartDate,
    watchAmenityId,
    type: AVAILABILITY_QUERY_TYPE_BLOCK,
    dispatch,
    active: true,
    setVerifyTimeslot
  });

  const { startTimeOptions, endTimeOptions, disabledDates, disabledEndDate } = useBlockDetailForm({
    amenity,
    availabilitySlots,
    watchStartTime,
    watchEndDate,
    watchStartDate,
    watchEndTime,
    watchAmenityId,
    watchBuilding,
    setValue,
    clearErrors,
    isFormInitialized: true
  });

  useEffect(() => {
    if (watchStartDate) {
      trigger('startDate');
    }
  }, [watchStartDate, amenity, trigger]);

  useEffect(() => {
    if (
      bookingStatus === requestSuccess(GET_AMENITY_AVAILABILITY_V2) ||
      bookingStatus === requestFail(GET_AMENITY_AVAILABILITY_V2)
    ) {
      setVerifyTimeslot(true);
    }
  }, [bookingStatus]);

  const showBlockModal = modal === 'modalAddBlock';

  useEffect(() => {
    if (showBlockModal) {
      setWizardStep(0);
    }
  }, [modal, showBlockModal]);

  const onClose = () => {
    dispatch(clearAmenity());
    dispatch(clearAmenityAvailabilityV2());
    dispatch(
      changeOpenedModal({
        modal: ''
      })
    );
    dispatch(clearBookingError());
    setWizardStep(0);
  };

  const customSubmit = (values) => {
    setIsSubmitting(true);

    dispatch(
      addBookingV3({
        body: buildBlockCreatePayload(values),
        success: () => {
          const latest = store.getState();
          const calendarSnap = latest.parAmBooking.lastBookingsCalendarV3Params;
          const buildingId = latest.building.globalBuildingId;
          onClose();
          if (calendarSnap && Number(calendarSnap.building) === Number(buildingId)) {
            dispatch(getBookingsCalendarV3(calendarSnap));
          }
          dispatch(
            getBookingsListV3(
              buildListV3Params({
                buildingId,
                page: 1,
                search: latest.parAmBooking.search,
                orgId: latest.Organization.globalOrgId
              })
            )
          );
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Block has been successfully created!', {
                defaultValue: 'Block has been successfully created!'
              })
            })
          );
          setIsSubmitting(false);
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to create block!', {
                defaultValue: 'Failed to create block!'
              }),
              modal: 'errorToast'
            })
          );
          setIsSubmitting(false);
        }
      })
    );
  };

  const handleChangeAmenity = useCallback(
    (amenityId) => {
      if (amenityId !== 'Please select' && parseInt(amenityId, 10) > 0) {
        dispatch(
          getAmenityV2({
            id: parseInt(amenityId, 10),
            fail: () => {
              dispatch(
                setToast({
                  toastShow: true,
                  toastMessage: t('Failed to get amenity!'),
                  modal: 'errorToast'
                })
              );
            }
          })
        );
      }
    },
    [dispatch, t]
  );

  useEffect(() => {
    if (watchAmenityId && parseInt(watchAmenityId, 10) > 0) {
      handleChangeAmenity(watchAmenityId);
    }
  }, [watchAmenityId, handleChangeAmenity]);

  const getFilteredAmenities = () => {
    if (currentAmenityType === 0) {
      return amenities.filter((map) => map.allow_booking);
    }

    return amenities.filter(
      (map) => parseInt(map?.type_id, 10) === currentAmenityType && map.allow_booking
    );
  };

  useEffect(() => {
    const building = watchBuilding;
    if (building?.value) {
      dispatch(getBuildingAmenitiesV2({ id: building?.value }));
    }
  }, [watchBuilding, dispatch]);

  useEffect(() => {
    const validateStartDate = async () => {
      await trigger(['startDate']);
    };
    if (watchStartDate && verifyTimeslot) {
      delay(function () {
        validateStartDate();
      }, 500);
    }
  }, [availabilitySlots, verifyTimeslot, trigger, watchStartDate]);

  const handleNext = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const fields = [
      'building',
      'amenity_id',
      'startDate',
      'endDate',
      'reason',
      'startTime',
      'endTime'
    ];
    const ok = await trigger(fields, { shouldFocus: true });
    if (ok) setWizardStep(1);
  };

  const onFormSubmit = (e) => {
    if (wizardStep !== 1) {
      e.preventDefault();
      return;
    }
    handleSubmit(customSubmit)(e);
  };

  const safeStep = Math.min(Math.max(0, wizardStep), BOOKING_WIZARD_STEP_COUNT - 1);
  const modalTitle = t('Blocks', { defaultValue: 'Blocks' });
  const wizardChromeProps = getAddBlockWizardChromeProps(safeStep, t, { modalTitle });

  return (
    <form name="AddBlockForm" onSubmit={onFormSubmit}>
      <WizardModalLayout
        Modal={StyledModalCardV2}
        modalProps={getBookingWizardModalProps(safeStep === 1)}
        show={showBlockModal}
        onClose={onClose}
        width="460px"
        bodyGap="12px"
        deps={[wizardStep, showBlockModal]}
        error={error}
        footerActions={
          wizardStep === 0 ? (
            <>
              <WizardFooterButtonSecondary type="button" onClick={onClose}>
                {t('Cancel')}
              </WizardFooterButtonSecondary>
              <WizardFooterButtonPrimary type="button" onClick={handleNext}>
                {t('Next', { defaultValue: 'Next' })}
              </WizardFooterButtonPrimary>
            </>
          ) : (
            <>
              <WizardFooterButtonSecondary type="button" onClick={() => setWizardStep(0)}>
                {t('Back')}
              </WizardFooterButtonSecondary>
              <WizardFooterButtonPrimary type="submit" disabled={isSubmitting}>
                {t('Confirm Block', { defaultValue: 'Confirm Block' })}
              </WizardFooterButtonPrimary>
            </>
          )
        }>
        <WizardChromeV2 {...wizardChromeProps}>
          {wizardStep === 0 && (
            <AddBlockDetailStepV2
              t={t}
              control={control}
              errors={errors}
              watch={watch}
              amenity={amenity}
              disabledDates={disabledDates}
              disabledEndDate={disabledEndDate}
              getFilteredAmenities={getFilteredAmenities}
              startTimeOptions={startTimeOptions}
              endTimeOptions={endTimeOptions}
              watchStartTime={watchStartTime}
              slotsLoading={slotsLoading}
            />
          )}

          {wizardStep === 1 && (
            <AddBlockConfirmationStepV2 t={t} getValues={getValues} amenities={amenities} />
          )}
        </WizardChromeV2>
      </WizardModalLayout>
    </form>
  );
};

export default AddBlockV2;
